/**
 * Axios-инстанс для всех запросов к бэку.
 *
 * - baseURL из VITE_API_URL; `withCredentials` — чтобы браузер прикладывал общую
 *   cookie Драфтера `.drafter.ru` (нужна для повторного обмена при истечении access).
 * - Request-interceptor подкладывает Authorization: Bearer (локальный access).
 * - Response-interceptor 401 → повтор обмена по общей cookie (exchange-drafter),
 *   затем ретрай запроса. Не вышло → уходим на вход `id.drafter.ru`.
 *
 * Refresh-токена нет: локальный access живёт 15 мин, при истечении молча
 * повторяем обмен (cookie Драфтера долгоживущая, живёт в браузере).
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { exchangeViaCookie, redirectToDrafterId } from "./drafter-auth";
import { tokenStorage } from "./token-storage";

const API_BASE: string = import.meta.env.VITE_API_URL || "/api";

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

/**
 * Один обмен параллельно — иначе пачка 401-запросов выпустит пачку обменов.
 */
let exchangePromise: Promise<string> | null = null;

async function reExchangeAccessToken(): Promise<string> {
  const { accessToken } = await exchangeViaCookie();
  tokenStorage.saveAccessToken(accessToken);
  return accessToken;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      try {
        exchangePromise ??= reExchangeAccessToken();
        const newToken = await exchangePromise;
        original.headers.set("Authorization", `Bearer ${newToken}`);
        return api.request(original);
      } catch (exchangeError) {
        tokenStorage.clear();
        redirectToDrafterId();
        return Promise.reject(exchangeError);
      } finally {
        exchangePromise = null;
      }
    }
    return Promise.reject(error);
  },
);
